import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Bridge component for the iOS manual call-log form (CONEK-4042).
//
// On `connect()`, asks the native shell for the most recent CallKit-
// observed call (contact phone, direction, started_at, duration). The
// shell replies with `{contact_id, direction, started_at, duration_ms}`
// or null; we prefill the form fields without overwriting anything the
// rep already typed. On submit success, fires `log_submitted` so the
// shell can dismiss any "you just hung up" notification UX.
//
// Graceful degradation: if no bridge is present, every method is a
// no-op and the form continues to work as a plain Hotwire form.
export default class extends BridgeComponent {
  static component = "call-log"

  static targets = [
    "form",
    "contactId",
    "direction",
    "startedAtDate",
    "startedAtTime",
    "durationMmss",
    "notes",
    "nextStep",
  ]

  connect() {
    super.connect()
    this.send("request_recent_call", {}, (message) => {
      this.handleRecentCall(message)
    })

    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", this.handleSubmit)
    }
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("submit", this.handleSubmit)
    }
    super.disconnect()
  }

  // The `contactId` target used to always be the `<input>` directly. It's
  // now `Ui::EntitySelectorComponent`'s outer wrapper div (CONEK-4566 3.6),
  // which holds its own value-carrying hidden input
  // (`data-ui--entity-selector-target="hidden"`) instead of exposing
  // `.value` itself. Resolve to whichever element actually carries the
  // value so both shapes (plain input, or component wrapper) work.
  contactIdInput() {
    const el = this.contactIdTarget
    if (el.tagName === "INPUT") return el
    return el.querySelector('[data-ui--entity-selector-target="hidden"]')
  }

  handleRecentCall(message) {
    if (!message) return
    // The bridge callback receives the full message envelope {id, component, event, data: {...}}.
    // Extract the payload; fall back to the raw argument for direct callers (tests, etc.).
    const data =
      message.data !== null &&
      message.data !== undefined &&
      typeof message.data === "object"
        ? message.data
        : message
    if (!data) return

    if (data.contact_id && this.hasContactIdTarget) {
      const input = this.contactIdInput()
      if (input && !input.value) {
        input.value = data.contact_id
      }
    }

    // Native sides (iOS CallLogBridgeComponent, Android CallLogBridgeComponent)
    // send `is_outgoing: Bool` per the bridge contract — derive the radio
    // value from it. Accept `direction` too as a forward-compat alias for
    // future callers that prefer the canonical string.
    const direction =
      typeof data.is_outgoing === "boolean"
        ? data.is_outgoing
          ? "outbound"
          : "inbound"
        : data.direction
    if (direction && this.hasDirectionTarget) {
      const radio = this.directionTarget.querySelector(
        `input[type="radio"][value="${direction}"]`,
      )
      if (radio && !radio.checked) {
        radio.checked = true
      }
    }

    if (data.started_at) {
      const date = new Date(data.started_at)
      if (!isNaN(date.getTime())) {
        if (this.hasStartedAtDateTarget && !this.startedAtDateTarget.value) {
          this.startedAtDateTarget.value = date.toISOString().slice(0, 10)
        }
        if (this.hasStartedAtTimeTarget && !this.startedAtTimeTarget.value) {
          const hh = String(date.getHours()).padStart(2, "0")
          const mm = String(date.getMinutes()).padStart(2, "0")
          this.startedAtTimeTarget.value = `${hh}:${mm}`
        }
      }
    }

    // Native sides ship `duration_seconds` (Long) per the bridge contract.
    // Accept `duration_ms` as a forward-compat fallback if a future caller
    // sends milliseconds directly.
    const totalSecs =
      typeof data.duration_seconds === "number"
        ? Math.round(data.duration_seconds)
        : typeof data.duration_ms === "number"
          ? Math.round(data.duration_ms / 1000)
          : null
    if (
      totalSecs &&
      this.hasDurationMmssTarget &&
      !this.durationMmssTarget.value
    ) {
      const m = Math.floor(totalSecs / 60)
      const s = totalSecs % 60
      this.durationMmssTarget.value = `${m}:${String(s).padStart(2, "0")}`
    }
  }

  // Arrow form so `this` stays bound through the event listener.
  handleSubmit = () => {
    const input = this.hasContactIdTarget ? this.contactIdInput() : null
    this.send("log_submitted", {
      contact_id: input ? input.value : null,
    })
  }
}
